import type { ChangeEvent } from 'react';
import type { WidgetStyles } from './style-types';
import { DEFAULT_WIDGET_STYLES, stylesToCSS } from './style-types';

interface Props {
  styles: WidgetStyles;
  onChange: (next: WidgetStyles) => void;
  /** Optional preview content (the widget icon/label) — purely decorative. */
  previewLabel?: string;
}

const COLOR_SWATCHES: Array<{ key: keyof WidgetStyles; label: string; defaultValue: string }> = [
  { key: 'backgroundColor', label: 'Hintergrund (Karte)', defaultValue: DEFAULT_WIDGET_STYLES.backgroundColor },
  { key: 'sectionBackgroundColor', label: 'Hintergrund (Sektion)', defaultValue: DEFAULT_WIDGET_STYLES.sectionBackgroundColor },
  { key: 'textColor', label: 'Textfarbe', defaultValue: DEFAULT_WIDGET_STYLES.textColor },
  { key: 'accentColor', label: 'Akzent', defaultValue: DEFAULT_WIDGET_STYLES.accentColor },
  { key: 'borderColor', label: 'Rahmen', defaultValue: DEFAULT_WIDGET_STYLES.borderColor },
];

/**
 * WYSIWYG-style editor for widget appearance (colors, spacing, shape).
 * Renders a live inline preview so the user sees changes immediately.
 */
export function StyleEditor({ styles, onChange, previewLabel = 'Widget-Vorschau' }: Props) {
  const setField = <K extends keyof WidgetStyles>(key: K, value: WidgetStyles[K] | undefined) => {
    const next = { ...styles };
    if (value === undefined || value === '' || (typeof value === 'number' && Number.isNaN(value))) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  const handleColor = (key: keyof WidgetStyles) => (e: ChangeEvent<HTMLInputElement>) => {
    setField(key, e.target.value);
  };

  const handleNumber = (key: 'borderRadius' | 'padding') => (e: ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    setField(key, Number.isFinite(n) ? n : undefined);
  };

  const handleOpacity = (e: ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    setField('opacity', Number.isFinite(n) ? n : undefined);
  };

  const previewCSS = stylesToCSS(styles);
  const sectionBg = styles.sectionBackgroundColor || DEFAULT_WIDGET_STYLES.sectionBackgroundColor;
  const isDirty =
    Object.keys(styles).length > 0 &&
    Object.entries(styles).some(([k, v]) => {
      const def = (DEFAULT_WIDGET_STYLES as Record<string, unknown>)[k];
      return v !== undefined && v !== def;
    });

  return (
    <div className="space-y-5">
      {/* Live preview */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
            Live-Vorschau
          </p>
          <button
            type="button"
            onClick={() => onChange({})}
            disabled={!isDirty}
            className="inline-flex items-center gap-1.5 rounded-md border border-surface-300 bg-surface-100 px-2.5 py-1 text-[11px] font-medium text-neutral-200 transition hover:border-brand-500 hover:text-brand-300 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-surface-300 disabled:hover:text-neutral-200"
            title="Alle Farben, Abstände und die Deckkraft auf die Standardwerte zurücksetzen"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
            </svg>
            Auf Standard zurücksetzen
          </button>
        </div>
        <div
          className="rounded-xl border border-dashed border-surface-400 p-4"
          style={{ backgroundColor: sectionBg }}
        >
          <div
            className="mx-auto flex h-24 max-w-sm items-center justify-center text-sm font-medium"
            style={{
              backgroundColor: DEFAULT_WIDGET_STYLES.backgroundColor,
              color: DEFAULT_WIDGET_STYLES.textColor,
              borderRadius: DEFAULT_WIDGET_STYLES.borderRadius,
              padding: DEFAULT_WIDGET_STYLES.padding,
              ...previewCSS,
            }}
          >
            {previewLabel}
          </div>
          <p className="mt-2 text-center text-[11px] text-neutral-400">
            So wird das Widget auf der Webseite angezeigt (Beispiel-Text).
          </p>
        </div>
      </div>

      {/* Colors */}
      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          Farben
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {COLOR_SWATCHES.map(({ key, label, defaultValue }) => {
            const current = (styles[key] as string | undefined) ?? '';
            return (
              <label key={key} className="flex flex-col gap-1.5">
                <span className="text-xs text-neutral-400">{label}</span>
                <div className="flex items-center gap-2 rounded-lg border border-surface-300 bg-surface-100 px-2 py-1.5">
                  <input
                    type="color"
                    value={current || defaultValue}
                    onChange={handleColor(key)}
                    className="h-6 w-6 cursor-pointer rounded border-0 bg-transparent p-0"
                  />
                  <input
                    type="text"
                    value={current}
                    onChange={handleColor(key)}
                    placeholder={defaultValue}
                    className="min-w-0 flex-1 bg-transparent text-xs text-neutral-200 placeholder:text-neutral-600 focus:outline-none"
                    maxLength={9}
                  />
                  {current && (
                    <button
                      type="button"
                      onClick={() => setField(key, undefined)}
                      className="text-[10px] text-neutral-500 hover:text-neutral-200"
                      aria-label={`${label} zurücksetzen`}
                    >
                      ×
                    </button>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Spacing & shape */}
      <div>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          Abstände &amp; Form
        </p>
        <div className="space-y-4">
          <SliderRow
            label="Innenabstand"
            value={styles.padding ?? 16}
            min={0}
            max={64}
            step={2}
            unit="px"
            onChange={handleNumber('padding')}
          />
          <SliderRow
            label="Eckenradius"
            value={styles.borderRadius ?? 16}
            min={0}
            max={48}
            step={1}
            unit="px"
            onChange={handleNumber('borderRadius')}
          />
          <SliderRow
            label="Deckkraft"
            value={styles.opacity ?? 1}
            min={0.3}
            max={1}
            step={0.05}
            unit=""
            onChange={handleOpacity}
            format={(v) => `${Math.round(v * 100)}%`}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => onChange({})}
        disabled={!isDirty}
        className="text-xs text-neutral-500 hover:text-neutral-300 underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:no-underline"
      >
        Alle Stile zurücksetzen
      </button>
    </div>
  );
}

interface SliderRowProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  format?: (v: number) => string;
}

function SliderRow({ label, value, min, max, step, unit, onChange, format }: SliderRowProps) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-28 shrink-0 text-xs text-neutral-400">{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={onChange}
        className="flex-1 accent-brand-500"
      />
      <span className="w-14 shrink-0 text-right text-xs tabular-nums text-neutral-300">
        {format ? format(value) : `${value}${unit}`}
      </span>
    </div>
  );
}
